/**
 * `<gs-cc-widget>` — Content projector for internal community widgets.
 *
 * Projects server-rendered content from a `<template shadowrootmode>` into
 * shadow DOM, injects design tokens, and hydrates Preact components.
 * Used standalone (when content is SSR'd) or inside `<gs-cc-embed-widget>`
 * (which fetches the content from the backend).
 */
export class WidgetElement extends HTMLElement {
  private pendingReadyHandler: (() => void) | null = null

  connectedCallback(): void {
    if (this.shadowRoot) {
      WidgetElement.injectDesignTokens(this.shadowRoot)
      this.hydratePreact(this.shadowRoot)
      return
    }

    const template = this.querySelector<HTMLTemplateElement>('template[shadowrootmode]')

    if (!template) {
      console.warn('[WidgetSDK] Widget: No shadow root or template found.')
      return
    }

    const mode = (template.getAttribute('shadowrootmode') ?? 'open') as ShadowRootMode
    const shadowRoot = this.attachShadow({ mode })
    shadowRoot.appendChild(template.content.cloneNode(true))
    template.remove()

    WidgetElement.injectDesignTokens(shadowRoot)
    this.hydratePreact(shadowRoot)
  }

  disconnectedCallback(): void {
    if (this.pendingReadyHandler) {
      document.removeEventListener('insidedReady', this.pendingReadyHandler)
      this.pendingReadyHandler = null
    }
    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = ''
    }
  }

  static injectDesignTokens(shadowRoot: ShadowRoot): void {
    if (shadowRoot.getElementById('css-variables')) return

    const source = document.getElementById('css-variables')
    if (!source || !(source instanceof HTMLStyleElement)) return

    const cssText = source.textContent
    if (!cssText) return

    const style = document.createElement('style')
    style.id = 'css-variables'
    style.textContent = cssText.replace(/html\s*\{/, ':host {')
    shadowRoot.prepend(style)
  }

  private hydratePreact(root: ShadowRoot): void {
    const insided = (globalThis as any).insided
    if (typeof insided?.bindPreactComponents === 'function') {
      insided.bindPreactComponents(root)
      return
    }

    // Resources not loaded yet — wait for the Preact runtime to signal readiness.
    // bootstrap.ts dispatches 'insidedReady' on document after initializeApp()
    // sets window.insided.bindPreactComponents.
    this.pendingReadyHandler = (): void => {
      this.pendingReadyHandler = null
      const ready = (globalThis as any).insided
      if (typeof ready?.bindPreactComponents === 'function') {
        ready.bindPreactComponents(root)
      }
    }
    document.addEventListener('insidedReady', this.pendingReadyHandler, { once: true })
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('gs-cc-widget')) {
  customElements.define('gs-cc-widget', WidgetElement)
}
